package dev.kuml.portal.web.editor

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element

/**
 * Click-to-select hit-testing for R-27's inlined SVG stage -- the thin DOM-touching counterpart to
 * the pure [DiagramSelection] state machine [contentFor][InspectorModel.contentFor] already builds
 * on. `EditorScreen.kt`'s single click listener on the stage wrapper calls [idAt] to resolve a raw
 * DOM click to a `data-kuml-id` (see `SvgInliner.kt`'s id-rewrite), then [transitionFor] to turn
 * that id into the next [SelectionState] -- this object never mutates selection state itself, it
 * only computes the transition, matching [DiagramSelection]'s own "pure value + pure transitions"
 * convention.
 */
public object DiagramCanvas {
    /** CSS class toggled by [applyHighlight] -- see `app.css`'s `.kportal-canvas-selected` rule. */
    public const val SELECTED_CLASS: String = "kportal-canvas-selected"

    /** CSS class toggled by [applyHighlight] for the selected EDGE -- see `app.css`'s
     * `.kportal-canvas-selected-edge` rule. Distinct from [SELECTED_CLASS]: an outline box (which
     * spans an edge's full diagonal bounding box and would visually cover unrelated nodes) is wrong
     * for a line/path/polygon, so edges get a stroke/fill recolor instead (see this file's own
     * [highlightClassFor]). */
    public const val SELECTED_EDGE_CLASS: String = "kportal-canvas-selected-edge"

    /**
     * The `data-kuml-id` of the element [target] (or one of its ancestors, via [Element.closest])
     * belongs to -- `null` for a click that landed on no such element at all (empty canvas
     * background, or outside the SVG entirely). [target] is ordinarily a click event's
     * `event.target as? Element`.
     */
    public fun idAt(target: Element): String? = target.closest("[data-kuml-id]")?.getAttribute("data-kuml-id")

    /**
     * The [SelectionState] transition a click on [id] produces. Whether [id] selects an EDGE or a
     * NODE is decided the same way [InspectorModel]/[DiagramOutlineModel.relationshipIdsOf] already
     * decide it -- by looking [id] up in [outline], never by any property of the click itself -- so
     * this function can never disagree with what the inspector panel renders for the resulting
     * selection.
     *
     * The `id in relationshipIdsOf(outline)` branch became reachable in 0.54.1 (fix/edge-svg-
     * element-ids in kuml-dev/kUML): every relationship renderer now wraps its output in
     * `<g id="…">` with the relationship's KumlElement.id, exactly like node renderers already
     * did -- see EdgeRendererDispatcher.dispatch in kuml-io-svg. No change needed here: this
     * function already resolved a relationship click correctly, it simply never received one
     * before this kUML version. [DiagramCanvasTest] covers it directly with a synthetic outline.
     *
     * **[id] is now validated against [nodeIdsOf] before ever being treated as a node click (review
     * fix, MINOR, 2026-09-02).** [DiagramOutlineModel.nodeIdsOf]'s own KDoc had, since it was
     * written, already claimed this validation happened here -- it did not; every id [idAt] resolved
     * that was not itself a relationship id fell straight into the node-click branches below,
     * unconditionally. `SvgInliner.kt`/`SvgSanitizePolicy.kt` had papered over the resulting false
     * positive for exactly three renderer-internal structural group ids (`"nodes"`, `"edges"`,
     * `"kuml-diagram-frame"`) by stripping their `id` attribute outright before it ever reached this
     * function -- which both MISSED every id this validation now also catches for free (a `kUML`
     * comment's `<g id="<commentId>">`, never surfaced by `/outline` -- `DiagramOutline.kt` discards
     * comments -- so `commentId` was never a member of [nodeIdsOf] either, and a shift-click
     * combining it with a real node silently corrupted that selection instead of being ignored) AND
     * was itself unsound the other way (a real classifier legitimately named e.g. `"nodes"` -- a
     * valid, non-empty [DiagramLoroMutator] name with no reserved-word check -- had its `id` stripped
     * by that same blocklist and became permanently unclickable/unhighlightable, despite existing in
     * the outline). Validating [id] against the outline directly, right here, fixes both directions
     * at once and makes the structural-id blocklist unnecessary: [SvgInliner.kt] now rewrites every
     * `id` to `data-kuml-id` uniformly again, and whatever of those [id]s is not actually a member of
     * [outline] -- structural or otherwise -- simply produces no selection change at all, exactly
     * like a click on empty canvas background always has.
     */
    public fun transitionFor(
        current: SelectionState,
        outline: DiagramOutlineResponse,
        id: String,
        shiftKey: Boolean,
    ): SelectionState =
        when {
            id in relationshipIdsOf(outline) -> DiagramSelection.selectEdge(current, id)
            id !in nodeIdsOf(outline) -> current
            shiftKey -> DiagramSelection.shiftClick(current, id)
            else -> DiagramSelection.click(current, id)
        }

    /**
     * Which highlight CSS class (if any) [id] should carry, given the current [selection] -- a pure
     * function so the node/edge distinction for highlighting is unit-testable without a DOM (see
     * [DiagramCanvasTest]). [applyHighlight] is the only caller.
     */
    internal fun highlightClassFor(
        selection: SelectionState,
        id: String,
    ): String? =
        when {
            selection.edgeId == id -> SELECTED_EDGE_CLASS
            id in selection.nodeIds -> SELECTED_CLASS
            else -> null
        }

    /**
     * Repaints [SELECTED_CLASS]/[SELECTED_EDGE_CLASS] onto exactly the `[data-kuml-id]` elements
     * [selection] currently selects inside [root] -- a full repaint of every such element on every
     * call, not an incremental diff against the previous selection: an inlined kUML diagram is small
     * enough (dozens, not thousands, of elements) that re-checking membership for all of them is
     * cheap, and a full repaint can never leave a stale highlight behind from a selection that
     * changed via [DiagramSelection.restore] (a collaborator's concurrent delete) rather than a
     * click this object itself resolved.
     */
    public fun applyHighlight(
        root: Element,
        selection: SelectionState,
    ) {
        val candidates = root.querySelectorAll("[data-kuml-id]")
        for (i in 0 until candidates.length) {
            val element = candidates.item(i) as? Element ?: continue
            val id = element.getAttribute("data-kuml-id")
            val target = id?.let { highlightClassFor(selection, it) }
            element.classList.toggle(SELECTED_CLASS, target == SELECTED_CLASS)
            element.classList.toggle(SELECTED_EDGE_CLASS, target == SELECTED_EDGE_CLASS)
        }
    }

    /**
     * The [SelectionState] `EditorScreen.kt`'s `handleClick` should commit to, given the raw
     * [directId] `idAt` resolved (`null` for a true background click, on no `[data-kuml-id]`
     * element at all) and the [resolvedId] the caller separately worked out from it (a direct
     * outline match, or [resolveEdgeByTolerance]'s nearby-edge search, or `null` if neither found
     * anything) -- pure, so this decision is unit-testable without a DOM (see
     * [DiagramCanvasTest]).
     *
     * **Review fix (2026-09-13):** [resolvedId] alone cannot tell a true background click
     * ([directId] `null`) apart from a click on an id the outline has never heard of (e.g. a kUML
     * comment's `<g id="<commentId>">` -- `/outline` never lists comments, see
     * `DiagramOutline.kt`, even though `comment(...)` is a first-class DSL construct and
     * `SvgSanitizePolicy.kt` leaves its `id` attribute reachable) that also happened to miss
     * [resolveEdgeByTolerance]'s tolerance ring. The two must NOT collapse to the same outcome:
     * a background click clears the selection ([DiagramSelection.escape]), matching a real
     * "click into empty space" intent, but a click on a known-but-unrecognized id must leave the
     * selection untouched -- exactly the contract [transitionFor] already guarantees for a direct
     * hit (see its own 2026-09-02 review-fix KDoc) and [DiagramCanvasTest]'s
     * `aClickOnAnIdNotInTheOutlineAtAllLeavesTheSelectionUnchanged` locks in. A prior revision of
     * `handleClick` called [transitionFor] only for a direct outline match and otherwise fell
     * through to [resolveEdgeByTolerance], treating that call's `null` result as "background" --
     * silently deselecting whatever was selected on a plain comment click that missed the
     * tolerance ring.
     */
    internal fun clickSelectionFor(
        current: SelectionState,
        outline: DiagramOutlineResponse,
        directId: String?,
        resolvedId: String?,
        shiftKey: Boolean,
    ): SelectionState =
        when {
            resolvedId != null -> transitionFor(current, outline, resolvedId, shiftKey)
            directId != null -> current
            else -> DiagramSelection.escape()
        }

    /** Radius (px) of the first (fine) tolerance ring [resolveEdgeByTolerance] samples under an
     * ordinary mouse pointer. */
    private const val EDGE_HIT_RADIUS_FINE_PX = 6.0

    /** Radius (px) of the second (coarse) tolerance ring [resolveEdgeByTolerance] samples under an
     * ordinary mouse pointer. */
    private const val EDGE_HIT_RADIUS_COARSE_PX = 12.0

    /** Radius (px) of the first (fine) tolerance ring [resolveEdgeByTolerance] samples under a
     * coarse (touch) pointer -- doubled vs. [EDGE_HIT_RADIUS_FINE_PX], matching the platform
     * convention that touch targets need roughly twice a mouse's precision margin. */
    private const val EDGE_HIT_RADIUS_FINE_COARSE_POINTER_PX = 12.0

    /** Radius (px) of the second (coarse) tolerance ring [resolveEdgeByTolerance] samples under a
     * coarse (touch) pointer -- doubled vs. [EDGE_HIT_RADIUS_COARSE_PX]. */
    private const val EDGE_HIT_RADIUS_COARSE_COARSE_POINTER_PX = 24.0

    /**
     * Resolves a click that landed on no `[data-kuml-id]` element at all, or on one whose id is
     * unknown to [outline] entirely, to a nearby EDGE only -- never a node (design-team review
     * ruling: the tolerance radius must never resolve to a node, only ever to a relationship, since a
     * node is already a large, easy click target and does not need this help). Samples 8 points at
     * 45° steps on two increasing radii (fine, then coarse -- doubled under a coarse pointer, i.e.
     * touch) around [clientX]/[clientY], and returns the id of the first sampled point whose
     * `[data-kuml-id]` ancestor is a member of [relationshipIdsOf]. `null` if nothing within
     * tolerance is an edge -- the caller must then leave the selection untouched, exactly like a
     * background click always has.
     *
     * A 1px association line with no arrowhead (ADR-0017, kUML 0.54.0) is not a reliable mouse
     * target on its own -- this tolerance ring is what makes clicking a plain association line
     * practical rather than requiring pixel-perfect precision.
     *
     * Presently load-bearing for every real relationship click, not just a UX nicety: kUML
     * 0.54.1's edge `<g id>` is double-XML-escaped for any id containing a reserved character
     * (see `gradle/libs.versions.toml`'s bump comment and `PortalRendererTest.kt` test 14), which
     * every real relationship id does -- so a browser's `data-kuml-id` for an edge never actually
     * equals [relationshipIdsOf]'s unescaped id, and [transitionFor]'s direct match never fires in
     * practice. This function is therefore what makes edge selection work at all today, until
     * that upstream bug is fixed.
     */
    internal fun resolveEdgeByTolerance(
        outline: DiagramOutlineResponse,
        clientX: Double,
        clientY: Double,
    ): String? {
        val edgeIds = relationshipIdsOf(outline)
        if (edgeIds.isEmpty()) return null
        val coarsePointer = window.matchMedia("(pointer: coarse)").matches
        val radii =
            if (coarsePointer) {
                listOf(EDGE_HIT_RADIUS_FINE_COARSE_POINTER_PX, EDGE_HIT_RADIUS_COARSE_COARSE_POINTER_PX)
            } else {
                listOf(EDGE_HIT_RADIUS_FINE_PX, EDGE_HIT_RADIUS_COARSE_PX)
            }
        for (radius in radii) {
            for (stepDeg in 0 until 360 step 45) {
                val rad = stepDeg * kotlin.math.PI / 180.0
                val x = clientX + radius * kotlin.math.cos(rad)
                val y = clientY + radius * kotlin.math.sin(rad)
                val candidate = document.elementFromPoint(x, y) ?: continue
                val id = idAt(candidate) ?: continue
                if (id in edgeIds) return id
            }
        }
        return null
    }
}
